import classnames from 'classnames';
import { ForwardedRef, forwardRef, PropsWithChildren, Ref, useMemo } from 'react';

import {
  radioGroupContext,
  RadioGroupContext,
} from '../../../Forms/RadioGroup/RadioButton/RadioButton.types';

import styles from './CardRadioGroup.module.scss';
import { CardRadioGroupProps } from './CardRadioGroup.types';

const CardRadioGroupInner = <T extends string, TClearable extends boolean = false>(
  {
    value,
    children,
    onValueChange,
    clearable,
    disabled,
    error,
    className,
  }: PropsWithChildren<CardRadioGroupProps<T, TClearable>>,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const radioGroup = useMemo(
    (): RadioGroupContext => ({
      selectedValue: value,
      onSelectValue: clickedValue => {
        // @ts-expect-error We can call onValueChange with undefined because we know that clearable is true
        if (clearable && clickedValue === value) onValueChange?.(undefined);
        else onValueChange?.(clickedValue as T);
      },
      disabled,
      error,
    }),
    [clearable, disabled, error, onValueChange, value]
  );

  return (
    <radioGroupContext.Provider value={radioGroup}>
      <div className={classnames(styles.cardRadioGroup, className)} ref={ref}>
        {children}
      </div>
    </radioGroupContext.Provider>
  );
};

export const CardRadioGroup = forwardRef(CardRadioGroupInner) as <
  T extends string,
  TClearable extends boolean = false,
>(
  props: PropsWithChildren<CardRadioGroupProps<T, TClearable>> & { ref?: Ref<HTMLDivElement> }
) => ReturnType<typeof CardRadioGroupInner>;
